import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CardMedia,
  Chip,
  CardActionArea,
  Fade,
} from '@mui/material';
import QuantitySelector from '../../../app/components/QuantitySelector';
import MealItem from '../../../app/data/models/mealItem';
import { useMealType } from '../../../app/data/models/mealTypes';
import MealItemCustomizationSelector from './MealItemCustomizationSelector';
import { CartItemCustomization } from '../../../app/data/models/cart';
import { useCustomizations } from '../../../hooks/useCustomizations';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../app/utils/formatters';

interface MealItemCardProps {
  mealItem: MealItem;
  quantity: number;
  onQuantityChange: (
    cartItemId: string,
    quantity: number,
    customizations?: CartItemCustomization[]
  ) => void;
  variant?: 'default' | 'compact';
  isAvailable?: boolean;
}

const MealItemCard = ({
  mealItem,
  quantity,
  onQuantityChange,
  variant = 'default',
  isAvailable = true,
}: MealItemCardProps) => {
  const [imageError, setImageError] = useState(false);
  const {
    showCustomizations,
    setShowCustomizations,
    handleQuantityChange,
    handleAddCustomizations,
  } = useCustomizations(mealItem, onQuantityChange);

  const { t } = useTranslation();

  if (variant === 'compact') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <QuantitySelector
          quantity={quantity}
          onQuantityChange={handleQuantityChange}
          hasCustomizations={!!mealItem.customizations?.length}
          sx={{
            minWidth: 120,
            '& .MuiButton-root': {
              px: 1,
            },
          }}
        />
      </Box>
    );
  }

  const mealType = useMealType(mealItem.mealType);

  return (
    <>
      <Card
        elevation={quantity > 0 ? 4 : 1}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s ease-in-out',
          opacity: isAvailable ? 1 : 0.7,
          '&:hover': isAvailable
            ? {
                transform: 'translateY(-4px)',
              }
            : undefined,
        }}
      >
        <CardActionArea>
          <Box sx={{ position: 'relative' }}>
            {imageError ? (
              <Box
                sx={{
                  height: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'action.hover',
                  textAlign: 'center',
                  p: 1,
                  overflow: 'hidden',
                }}
                role="img"
                aria-label={mealItem.name}
              >
                <Typography variant="body2" color="text.secondary">
                  {mealItem.name}
                </Typography>
              </Box>
            ) : (
              <CardMedia
                component="img"
                alt={mealItem.name}
                height="200"
                image={
                  mealItem.pictureUrl || '/assets/images/meal-placeholder.svg'
                }
                sx={{
                  objectFit: 'cover',
                  filter: !isAvailable ? 'grayscale(100%)' : 'none',
                }}
                onError={() => setImageError(true)}
              />
            )}

            {!isAvailable && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  p: 1,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t('restaurant.item-unavailable')}
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              display: 'flex',
              gap: 1,
            }}
          >
            <Chip
              label={mealType.name}
              size="small"
              sx={{
                bgcolor: `${mealType.color}`,
                color: 'white',
              }}
            />
          </Box>
        </CardActionArea>

        <CardContent
          sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
        >
          <Typography gutterBottom variant="h6" component="div">
            {mealItem.name}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, flexGrow: 1 }}
          >
            {mealItem.description}
          </Typography>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body1" color="text.primary" fontWeight={500}>
              {formatCurrency(mealItem.price)}
            </Typography>
            {isAvailable && (
              <Fade in={true}>
                <Box>
                  <QuantitySelector
                    quantity={quantity}
                    onQuantityChange={handleQuantityChange}
                    hasCustomizations={!!mealItem.customizations?.length}
                  />
                </Box>
              </Fade>
            )}
          </Box>
        </CardContent>
      </Card>
      <MealItemCustomizationSelector
        open={showCustomizations}
        onClose={() => setShowCustomizations(false)}
        customizations={mealItem.customizations || []}
        onAddToCart={handleAddCustomizations}
        itemName={mealItem.name}
        itemPrice={mealItem.price}
        mealItemId={mealItem.id}
      />
    </>
  );
};

export default MealItemCard;
